import React, { useEffect } from 'react';

/**
 * IB-701 — generic right-anchored slide-over drawer for the redesigned Procurement face. Used by
 * the Setup tab to host the existing Policy / Category CRUD forms without leaving the dashboard.
 *
 * Presentation is modelled on components/budgets/VersionHistoryDrawer (scrim + Escape close, fixed
 * right panel, scrollable body), but kept generic with a header title + body slot so it can wrap
 * any content. VersionHistoryDrawer itself is intentionally left untouched.
 *
 * `footer` renders a fixed action bar under the scrolling body; `widthClassName` and
 * `titleClassName` replace the default width and title styling.
 */
const SlideOver = ({
    title,
    subtitle,
    isOpen,
    onClose,
    children,
    footer = null,
    widthClassName = 'tw-max-w-md',
    titleClassName = 'tw-text-lg tw-font-bold tw-text-charcoal',
}) => {
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <div className="tw-fixed tw-inset-0 tw-z-90 tw-bg-black/40" onClick={onClose} aria-hidden="true" />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className={`tw-fixed tw-inset-y-0 tw-end-0 tw-z-100 tw-flex tw-h-full tw-w-full ${widthClassName} tw-flex-col tw-bg-white tw-shadow-primary`}
            >
                <div className="tw-relative tw-border-b tw-border-grey tw-px-6 tw-py-5">
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={$t('Close')}
                        className="tw-absolute tw-end-4 tw-top-4 tw-text-xl tw-leading-none tw-text-charcoal-50 hover:tw-text-charcoal"
                    >
                        ✕
                    </button>
                    <div className={titleClassName}>{title}</div>
                    {subtitle && <div className="tw-mt-1 tw-text-xs tw-text-charcoal-50">{subtitle}</div>}
                </div>

                <div className="tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-5">{children}</div>

                {footer && <div className="tw-flex tw-justify-end tw-gap-3 tw-border-t tw-border-grey tw-px-6 tw-py-4">{footer}</div>}
            </div>
        </>
    );
};

export default SlideOver;
